import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Asset, type AssetKind } from "../api.js";
import { Button, COLORS, Empty, Input } from "../ui.js";
import { KIND_ICON, KIND_LABEL, humanSize } from "../lib/assetUpload.js";

interface GiriFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  durationSec: number | null;
  thumbnailUrl: string | null;
  kind: AssetKind;
  createdAt: string;
}

interface Props {
  onClose: () => void;
  onImported?: (asset: Asset) => void;
}

export function GiriDrivePicker({ onClose, onImported }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<AssetKind | "ALL">("ALL");
  const [importing, setImporting] = useState<Record<string, "doing" | "done" | "error">>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["giri-drive", kind, search],
    queryFn: () =>
      api.get<{ items: GiriFile[] }>(
        `/giri-drive/?${new URLSearchParams({
          ...(kind !== "ALL" ? { kind } : {}),
          ...(search ? { search } : {}),
          take: "60",
        })}`
      ),
  });

  const importOne = useMutation({
    mutationFn: (giriFileId: string) =>
      api.post<{ asset: Asset }>("/giri-drive/import", { giriFileId }),
    onMutate: (id) => setImporting((m) => ({ ...m, [id]: "doing" })),
    onSuccess: (res, id) => {
      setImporting((m) => ({ ...m, [id]: "done" }));
      qc.invalidateQueries({ queryKey: ["assets"] });
      onImported?.(res.asset);
    },
    onError: (_e, id) => setImporting((m) => ({ ...m, [id]: "error" })),
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "min(900px, 100%)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
            ☁️ Importer depuis Giri&nbsp;Drive
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              color: COLORS.muted,
            }}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div
          style={{
            padding: "12px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {(["ALL", "VIDEO", "AUDIO", "PDF", "IMAGE"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              style={{
                padding: "5px 12px",
                border: `1px solid ${kind === k ? COLORS.accent : COLORS.border}`,
                borderRadius: 999,
                background: kind === k ? COLORS.accent : "transparent",
                color: kind === k ? "#fff" : COLORS.text,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "inherit",
              }}
            >
              {k === "ALL" ? "Tous" : KIND_LABEL[k]}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans Giri Drive…"
            style={{ width: 240 }}
          />
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {isLoading && <Empty>Chargement de Giri Drive…</Empty>}
          {error && (
            <Empty>
              Impossible de joindre Giri Drive — vérifie le token côté serveur.
            </Empty>
          )}
          {data?.items.length === 0 && <Empty>Aucun fichier trouvé.</Empty>}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 14,
            }}
          >
            {data?.items.map((f) => {
              const state = importing[f.id];
              return (
                <div
                  key={f.id}
                  style={{
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      height: 110,
                      background: f.thumbnailUrl
                        ? `#000 url(${f.thumbnailUrl}) center/cover`
                        : "#1a1a1a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 30,
                    }}
                  >
                    {!f.thumbnailUrl && KIND_ICON[f.kind]}
                  </div>
                  <div
                    style={{
                      padding: 10,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      title={f.name}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.name}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>
                      {KIND_LABEL[f.kind]} · {humanSize(f.sizeBytes)}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Button
                        onClick={() => importOne.mutate(f.id)}
                        disabled={state === "doing" || state === "done"}
                        style={{
                          padding: "5px 10px",
                          fontSize: 11,
                          width: "100%",
                        }}
                      >
                        {state === "doing"
                          ? "Import…"
                          : state === "done"
                            ? "✓ Importé"
                            : state === "error"
                              ? "Erreur — réessayer"
                              : "Importer"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
